import dayjs from 'dayjs';
import { orderBy } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { Badge, Breadcrumb, Stack } from 'react-bootstrap';

import LeftContent from '@components/Layout/LeftContent/LeftContent';
import RightContent from '@components/Layout/RightContent/RightContent';
import { SOLO_CAMPAIGN_CODES } from '@constants/clinician';
import { DRAFT_ORDERS, ORDERS_AND_REPORTS } from '@constants/routes';
import { Requisition } from '@customTypes/requisition';
import { isOrderReported } from '@helpers/order';
import { hasPatientResultsSummary } from '@helpers/patientResultsSummary';
import { usePatientDetail, useSettings } from '@hooks';
import { Context } from '@store/store';

import {
  PatientDetailDocuments,
  PatientDetailEligibilityBanners,
  PatientDetailOrders,
  PatientDetailPGx,
  PatientDetailReports,
  PatientDetailResultsSummary,
  PatientDetailSidebar,
  PatientDetailSkeleton,
} from './components';
import './PatientDetail.scss';

export const PatientDetail: React.FC = () => {
  const { isNatera, isNateraProd } = useSettings();

  const params = useParams();
  const navigate = useNavigate();
  const { state: locationState } = useLocation();

  const patientId = useMemo(() => params?.id, [params]);
  // Array of requisitions which includes at least one order to download
  const [filteredRequisitions, setFilteredRequisitions] = useState<Requisition[]>([]);
  const [state] = useContext(Context);

  const {
    requisitions,
    patientDetailInfo: patientDetail,
    hasGuidance,
    loading: patientDetailLoading,
    patientName,
    rerequisitionEligibility,
    loadPatientDetailData,
  } = usePatientDetail(patientId);

  useEffect(() => {
    if (patientId) {
      loadPatientDetailData();
    }
  }, [patientId, loadPatientDetailData]);

  const isUnder18 = useMemo(() => {
    if (!patientDetail?.demographics?.dob) return false;

    return dayjs().diff(patientDetail?.demographics.dob, 'year') < 18;
  }, [patientDetail?.demographics?.dob]);

  const moveTo = () => {
    navigate(locationState?.prevPath ?? ORDERS_AND_REPORTS);
  };

  const loading = useMemo(() => patientDetailLoading || !patientDetail, [patientDetailLoading, patientDetail]);

  const [debouncedLoading, setDebouncedLoading] = useState(loading);

  const allowGeneralRerequisition = useMemo(() => {
    const campaigns = state.userProfile?.campaigns;
    if (!campaigns || campaigns.length === 0) return true; // default allow when no solo campaign
    return !campaigns.some(c => SOLO_CAMPAIGN_CODES.includes(c.campaign_code));
  }, [state.userProfile?.campaigns]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedLoading(loading);
    }, 500);

    return () => clearTimeout(handler);
  }, [loading]);

  const patientInfoCells = useMemo(
    () => [
      {
        label: 'Date of Birth',
        value: patientDetail?.demographics?.dob,
      },
      {
        label: 'Sex',
        value: patientDetail?.demographics?.sex,
      },
    ],
    [patientDetail],
  );

  const totalDocuments = useMemo(() => {
    let totalNum = 0;

    filteredRequisitions.forEach(requisition => {
      totalNum += requisition.orders.length;

      if (requisition.clinical_summary_letter?.uri) {
        totalNum += 1;
      }
    });

    return totalNum;
  }, [filteredRequisitions]);

  useEffect(() => {
    setFilteredRequisitions(() =>
      orderBy(
        requisitions
          .map(requisition => ({
            ...requisition,
            orders: requisition.orders.filter(order => isOrderReported(order)),
          }))
          .filter(requisition => requisition.orders.length > 0),
        ['requisition_id'],
        ['desc'],
      ),
    );
  }, [requisitions]);

  const showResultsSummary = useMemo(() => hasPatientResultsSummary(requisitions), [requisitions]);

  if (!patientId) {
    moveTo();
    return null;
  }

  const showPgxGuidance = !isNatera;

  return (
    <div className='d-flex flex-column flex-lg-row page-content patient-detail-page'>
      <LeftContent>
        <PatientDetailSidebar
          hasPartnershipOrdering={state.hasPartnershipOrdering}
          loading={debouncedLoading}
          numOfDocuments={totalDocuments}
          numOfOrders={requisitions.length}
          showPGxGuidance={showPgxGuidance}
          showResultsSummary={showResultsSummary}
        />
      </LeftContent>

      <RightContent>
        {debouncedLoading ? (
          <PatientDetailSkeleton />
        ) : (
          <Stack gap={4} className='patient-detail-content'>
            <Breadcrumb listProps={{ className: 'mb-0' }}>
              <Breadcrumb.Item onClick={moveTo}>
                {locationState?.prevPath === DRAFT_ORDERS ? 'Draft Orders' : 'Orders and Reports'}
              </Breadcrumb.Item>
              <Breadcrumb.Item active>Patient Detail</Breadcrumb.Item>
            </Breadcrumb>

            <Stack as='section' className='box-shadow-50 bg-white rounded-1 patient-detail-info'>
              {isUnder18 && (
                <Badge
                  as='p'
                  bg='info-subtle'
                  text='info-emphasis'
                  className='align-self-start mb-0 fs-sm patient-age-min'
                >
                  Patient is under 18 years old.
                </Badge>
              )}

              <h1 className='mb-3 mb-lg-4 page-title' data-openreplay-sanitize>
                {patientName}
              </h1>

              <ul className='d-flex gap-3 ps-0 mb-0 patient-detail-cells'>
                {patientInfoCells.map(infoCell => (
                  <li key={infoCell.label} className='d-flex flex-column align-items-start patient-detail-cell'>
                    <p className='mb-0 text-secondary cell-label'>{infoCell.label}</p>

                    <p className='lh-xs fw-medium mb-0 cell-value' data-openreplay-sanitize>
                      {infoCell.value}
                    </p>
                  </li>
                ))}
              </ul>

              <PatientDetailEligibilityBanners eligibility={rerequisitionEligibility} />
            </Stack>

            <PatientDetailResultsSummary requisitions={requisitions} />

            <PatientDetailOrders requisitions={requisitions} />

            <PatientDetailDocuments requisitions={filteredRequisitions} numOfDocuments={totalDocuments} />

            {!isNateraProd && (
              <PatientDetailReports
                allowGeneralRerequisition={allowGeneralRerequisition}
                rerequisitionEligibility={rerequisitionEligibility}
              />
            )}

            {showPgxGuidance && <PatientDetailPGx patientId={patientId} hasGuidance={hasGuidance} />}
          </Stack>
        )}
      </RightContent>
    </div>
  );
};
